import { type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import {
  ANNOTATION_TOOL_ADMIN_PROJECTS_PATH,
  ANNOTATION_TOOL_ADMIN_SUPERVISOR_REVIEW_PATH,
  ANNOTATION_TOOL_ANNOTATION_PATH,
  ANNOTATION_TOOL_ANNOTATOR_PROJECTS_PATH,
  ANNOTATION_TOOL_BASE_PATH,
  ANNOTATION_TOOL_DASHBOARD_PATH,
  ANNOTATION_TOOL_METRICS_DASHBOARD_PATH,
  ANNOTATION_TOOL_METRICS_ENCOUNTER_STATS_PATH,
  ANNOTATION_TOOL_METRICS_HOLIDAYS_PATH,
  ANNOTATION_TOOL_METRICS_IRR_SCORE_PATH,
  ANNOTATION_TOOL_METRICS_PATH,
  ANNOTATION_TOOL_METRICS_SUMMARY_PATH,
  ANNOTATION_TOOL_ROLE_MAPPING,
  ANNOTATION_TOOL_SUPERVISOR_PATH,
  ANNOTATION_TOOL_SUPERVISOR_PROJECTS_PATH,
  AnnotationToolFeatureId,
  AnnotationToolSidebarAppID,
  getAnnotationToolAdminSupervisorReviewPath,
  getAnnotationToolDashboardPath,
  getAnnotationToolMetricsDashboardPath,
  useAuth,
  withAuth,
} from '@verantos-dev/core-annotation-tool';
import { type UserRolesIdsEnum } from '@verantos-dev/core-enums';
import {
  Dashboard,
  useGetHasAnnotationTasksQuery,
  useGetHasSupervisionBatchesQuery,
} from '@verantos-dev/feature-annotation-dashboard';
import {
  MetricDashboard,
  MetricEncounterStats,
  MetricHolidays,
  MetricIRRScore,
  MetricSummary,
  ProjectMetrics,
} from '@verantos-dev/feature-annotation-metrics';
import {
  AdminProjectsScreen,
  CreateProjectWizardScreen,
  GenerateAccuracyScreen,
  ProjectDetails,
} from '@verantos-dev/feature-annotation-project-admin';
import { AnnotationScreen } from '@verantos-dev/feature-annotation-screen';
import { AnnotatorProjectsScreen } from '@verantos-dev/feature-annotator-projects';
import { AnnotatorQueue } from '@verantos-dev/feature-annotator-queue';
import {
  SupervisorProjectDetails,
  SupervisorProjectOverview,
  SupervisorProjectsScreen,
} from '@verantos-dev/feature-supervisor-projects';
import { SupervisorQueue } from '@verantos-dev/feature-supervisor-queue';
import { SupervisorModeProvider, SupervisorScreen } from '@verantos-dev/feature-supervisor-screen';

import { AnnotationToolNoAccessPage } from './AnnotationToolNoAccessPage';

const APP_ID = AnnotationToolSidebarAppID.annotationTool;

// Curries the shared withAuth's fallbackRoute to '/no-access' and opts into
// history replace on redirect, so neither needs repeating at every call site.
const withAnnotationAuth = <P extends object>(
  Component: ComponentType<P>,
  appId?: string,
  claimCheckDefinitionMap?: Record<string, UserRolesIdsEnum | UserRolesIdsEnum[]>
) => withAuth(Component, appId, claimCheckDefinitionMap, '/no-access', true);

// Create base protected components with Auth0 role checks
const BaseProtectedSupervisorQueue = withAnnotationAuth(
  SupervisorQueue,
  AnnotationToolFeatureId.annotationSupervisor,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseProtectedAnnotatorQueue = withAnnotationAuth(
  AnnotatorQueue,
  AnnotationToolFeatureId.annotation,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseProtectedAdminProjects = withAnnotationAuth(
  AdminProjectsScreen,
  AnnotationToolFeatureId.adminProjects,
  ANNOTATION_TOOL_ROLE_MAPPING
);
// Shares admin-projects' access rule rather than a second AnnotationToolFeatureId entry —
// the form is a child of admin-projects. The feature flag is checked inside the screen,
// not here, since useFeatureFlagSplit reads false until the Split SDK is ready.
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
const BaseProtectedGenerateAccuracy = withAnnotationAuth(
  GenerateAccuracyScreen,
  AnnotationToolFeatureId.adminProjects,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseProjectAnnotatorProjects = withAnnotationAuth(
  AnnotatorProjectsScreen,
  AnnotationToolFeatureId.annotatorProjects,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseProjectSupervisorProjects = withAnnotationAuth(
  SupervisorProjectsScreen,
  AnnotationToolFeatureId.supervisorProjects,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseMetrics = withAnnotationAuth(
  MetricDashboard,
  AnnotationToolFeatureId.metrics,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseMetricsSummary = withAnnotationAuth(
  MetricSummary,
  AnnotationToolFeatureId.metrics,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseMetricsEncounterStats = withAnnotationAuth(
  MetricEncounterStats,
  AnnotationToolFeatureId.metrics,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseMetricsIRRScore = withAnnotationAuth(
  MetricIRRScore,
  AnnotationToolFeatureId.metrics,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const BaseMetricsHolidays = withAnnotationAuth(
  MetricHolidays,
  AnnotationToolFeatureId.metrics,
  ANNOTATION_TOOL_ROLE_MAPPING
);

// Wrapper component for SupervisorQueue with additional API check
const SupervisorQueueWithApiCheck = () => {
  const { user } = useAuth();
  const { data: hasSupervisionBatchesData, isLoading } = useGetHasSupervisionBatchesQuery(
    undefined,
    { skip: !user.isUser }
  );

  // Admins always have access to the supervisor queue (they can view all projects)
  if (user.isAdmin) {
    return <BaseProtectedSupervisorQueue />;
  }

  if (isLoading) {
    return null;
  }

  if (!hasSupervisionBatchesData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseProtectedSupervisorQueue />;
};

// Regular supervisor review — redirects admin-only users to read-only route
const RegularSupervisorReview = () => {
  const { user } = useAuth();
  const { encounterId } = useParams();
  const location = useLocation();

  if (!user.isUser) {
    return (
      <Navigate
        to={`${getAnnotationToolAdminSupervisorReviewPath()}/review/${encounterId}${
          location.search
        }`}
        replace
      />
    );
  }

  return (
    <SupervisorModeProvider isReadOnly={false}>
      <SupervisorScreen />
    </SupervisorModeProvider>
  );
};

// Admin read-only supervisor review — only accessible to admins
const AdminSupervisorReview = () => {
  const { user } = useAuth();

  if (!user.isAdmin) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return (
    <SupervisorModeProvider isReadOnly={true}>
      <SupervisorScreen />
    </SupervisorModeProvider>
  );
};

// Wrapper component for SupervisorProjectsScreen with additional API check
const SupervisorProjectsWithApiCheck = () => {
  const { user } = useAuth();
  const { data: hasSupervisionTasksData, isLoading: isSupervisionLoading } =
    useGetHasSupervisionBatchesQuery();
  const { data: hasAnnotationTasksData, isLoading: isAnnotationLoading } =
    useGetHasAnnotationTasksQuery(undefined, { skip: !user.isUser });

  if (isSupervisionLoading || (user.isUser && isAnnotationLoading)) {
    return null; // Or a loading spinner
  }

  const hasSupervision = hasSupervisionTasksData?.data?.has === true;
  const hasAnnotation = hasAnnotationTasksData?.data?.has === true;

  // ANNOTATION_MANAGER + ANNOTATION_USER without supervision batches but with annotation tasks
  // still routes "My Projects" here (Supervisor Projects), not to Annotator Projects.
  const isManagerWithAnnotationTasks =
    user.isAdmin && user.isUser && !hasSupervision && hasAnnotation;

  if (!hasSupervision && !isManagerWithAnnotationTasks) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseProjectSupervisorProjects />;
};

const MetricsWithApiCheck = () => {
  const { data: hasSupervisionTasksData, isLoading } = useGetHasSupervisionBatchesQuery();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!hasSupervisionTasksData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseMetrics />;
};

const MetricsSummaryWithApiCheck = () => {
  const { data: hasSupervisionTasksData, isLoading } = useGetHasSupervisionBatchesQuery();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!hasSupervisionTasksData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseMetricsSummary />;
};

const MetricsEncounterStatsWithApiCheck = () => {
  const { data: hasSupervisionTasksData, isLoading } = useGetHasSupervisionBatchesQuery();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!hasSupervisionTasksData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseMetricsEncounterStats />;
};

const MetricsIRRScoreWithApiCheck = () => {
  const { data: hasSupervisionTasksData, isLoading } = useGetHasSupervisionBatchesQuery();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!hasSupervisionTasksData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseMetricsIRRScore />;
};

const MetricsHolidaysWithApiCheck = () => {
  const { data: hasSupervisionTasksData, isLoading } = useGetHasSupervisionBatchesQuery();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!hasSupervisionTasksData?.data?.has) {
    return <Navigate to={getAnnotationToolDashboardPath()} replace />;
  }

  return <BaseMetricsHolidays />;
};

// Create protected route components
const ProtectedDashboard = withAnnotationAuth(
  Dashboard,
  AnnotationToolFeatureId.annotationDashboard,
  ANNOTATION_TOOL_ROLE_MAPPING
);
const ProtectedSupervisorQueue = SupervisorQueueWithApiCheck;
// Annotator Workspace pages (My Projects, My Assignments) are shown to any ANNOTATION_USER
// regardless of whether they currently have annotation tasks (counts may be 0), so access is
// role-gated only — no additional "has active work" check.
const ProtectedAnnotatorQueue = BaseProtectedAnnotatorQueue;
const ProtectedAdminProjects = BaseProtectedAdminProjects;
const ProtectedAnnotatorProjects = BaseProjectAnnotatorProjects;
const ProtectedSupervisorProjects = SupervisorProjectsWithApiCheck;
const ProtectedMetrics = MetricsWithApiCheck;
const ProtectedMetricsSummary = MetricsSummaryWithApiCheck;
const ProtectedMetricsEncounterStats = MetricsEncounterStatsWithApiCheck;
const ProtectedMetricsIRRScore = MetricsIRRScoreWithApiCheck;
const ProtectedMetricsHolidays = MetricsHolidaysWithApiCheck;

export const AnnotationToolUiRoutes = () => {
  return (
    <Routes>
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ANNOTATION_PATH}/annotation/:taskId`}
        element={<AnnotationScreen />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_SUPERVISOR_PATH}/review/:encounterId`}
        element={<RegularSupervisorReview />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_SUPERVISOR_REVIEW_PATH}/review/:encounterId`}
        element={<AdminSupervisorReview />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_DASHBOARD_PATH}`}
        element={<ProtectedDashboard />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_SUPERVISOR_PATH}`}
        element={<ProtectedSupervisorQueue />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ANNOTATION_PATH}`}
        element={<ProtectedAnnotatorQueue />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ANNOTATOR_PROJECTS_PATH}`}
        element={<ProtectedAnnotatorProjects />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_SUPERVISOR_PROJECTS_PATH}/project-details/:projectId`}
        element={<SupervisorProjectDetails />}
      >
        <Route index element={<SupervisorProjectOverview />} />
        <Route path="metrics/:metricsView" element={<ProjectMetrics />} />
      </Route>
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_SUPERVISOR_PROJECTS_PATH}`}
        element={<ProtectedSupervisorProjects />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_PROJECTS_PATH}/create/:projectId`}
        element={<CreateProjectWizardScreen />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_PROJECTS_PATH}/create`}
        element={<CreateProjectWizardScreen />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_PROJECTS_PATH}/project-details/:projectId`}
        element={<ProjectDetails />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_PROJECTS_PATH}/generate-accuracy/:projectId`}
        element={<BaseProtectedGenerateAccuracy />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_ADMIN_PROJECTS_PATH}`}
        element={<ProtectedAdminProjects />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_PATH}`}
        element={<Navigate to={getAnnotationToolMetricsDashboardPath()} replace />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_DASHBOARD_PATH}`}
        element={<ProtectedMetrics />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_SUMMARY_PATH}`}
        element={<ProtectedMetricsSummary />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_ENCOUNTER_STATS_PATH}`}
        element={<ProtectedMetricsEncounterStats />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_IRR_SCORE_PATH}`}
        element={<ProtectedMetricsIRRScore />}
      />
      <Route
        path={`${ANNOTATION_TOOL_BASE_PATH}/${ANNOTATION_TOOL_METRICS_HOLIDAYS_PATH}`}
        element={<ProtectedMetricsHolidays />}
      />
      <Route path="no-access" element={<AnnotationToolNoAccessPage />} />
      <Route path="*" element={<Navigate to={getAnnotationToolDashboardPath()} replace />} />
    </Routes>
  );
};

export default withAnnotationAuth(AnnotationToolUiRoutes, APP_ID, ANNOTATION_TOOL_ROLE_MAPPING);
